import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { cancelBooking, fetchBookingData, formatFirebaseError } from "../api";
import { applySalonBranding } from "../branding";
import { detectLocale, t, type Locale } from "../i18n";
import { LegalFooter } from "../LegalFooter";
import type { BookingData } from "../types";

export function CancelPage() {
  const { code: salonCode = "", appointmentId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") ?? "";
  const [locale] = useState<Locale>(detectLocale());
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [salonName, setSalonName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyCanceled, setAlreadyCanceled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBookingData(salonCode);
        if (!cancelled) {
          setBookingData(data);
          setSalonName(data.details.name);
        }
      } catch {
        if (!cancelled) setError(t(locale, "salonNotFound"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonCode, locale]);

  useEffect(() => {
    applySalonBranding(bookingData?.branding ?? null);
  }, [bookingData?.branding]);

  async function handleCancel() {
    if (!token) {
      setError(t(locale, "cancelInvalid"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await cancelBooking({ salonCode, appointmentId, token });
      if (result.alreadyCanceled) {
        setAlreadyCanceled(true);
      }
      setDone(true);
    } catch (e) {
      const message = formatFirebaseError(e);
      if (message.includes("Invalid cancellation token")) {
        setError(t(locale, "cancelInvalid"));
      } else if (message.includes("Cancellation is only allowed")) {
        setError(t(locale, "cancelTooLate"));
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page center">
        <p>{t(locale, "loading")}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="card">
        <h2>{t(locale, "cancelTitle")}</h2>
        {salonName && <p className="muted">{salonName}</p>}

        {done ? (
          <p>
            {alreadyCanceled ? t(locale, "cancelAlready") : t(locale, "cancelSuccess")}
          </p>
        ) : (
          <>
            <p className="muted">#{appointmentId}</p>
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <button
                type="button"
                className={`btn btn-primary${submitting ? " loading" : ""}`}
                onClick={handleCancel}
                disabled={submitting || !token}
              >
                {submitting ? t(locale, "loading") : t(locale, "cancelConfirm")}
              </button>
            </div>
          </>
        )}
      </div>
      <p className="footer">{t(locale, "poweredBy")} WERKSTATE</p>
      <LegalFooter locale={locale} />
    </div>
  );
}
